import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../types-enums/activity";
import {isValidDay} from "../components/journey/common";
import {cloneDeep, isEqual} from "lodash";
import {DurationProps} from "../types-enums/activity-common-properties";
import {getDurationInDaysOrNights} from "../utils/getter/common";
import {
    isActivityFlightState,
    isFlightCalculationRequestLeg,
    isGeneralCalculatedFlightEmissionsResponse
} from "../utils/check/type-guards";
import {useRef, useState} from "react";
import {FlightCalculationRequestLeg, FlightsResponse} from "../api/model/activity-flight";
import {convertBackendStringDateToDateTime} from "../utils/converter/dates";
import {constructGetFlightByFlightNumberQuery} from "../utils/string/api";
import Cookies from "js-cookie";
import {COOKIE_NAME} from "../constants/default-values";
import {GET_FLIGHTS_BY_DATE_AND_AIRLINE} from "../api/endpoints";
import {generateConnectionStringFromCalculationResponseString} from "../utils/string/flight";
import {useCalculateEmissionsMutation} from "../redux/reducer/api";
import {CalculatedEmissionsBackendResponse, CalculateEmissionsRequest} from "../api/model/emissions";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL} from "../api/model/common";
import {recalculateActivityOnDifferentDate} from "../utils/check/activities";
import {ActivityFlightState} from "../types-enums/activity-flight";
import {useTranslation} from "react-i18next";
import {TypeOptions} from "react-toastify";
import {formatDateString, generateTranslatedActivityTypeString} from "../utils/string/common";
import {updateActivityRequestWithNewDate} from "../utils/setter/activities";


export interface UseUpdateActivityOnDifferentDateProps {
    journeyDuration: DurationProps,
    renderToast: (message: string, type: TypeOptions) => void
}

const ERROR_CODES_TO_REMOVE_FLIGHT_NUMBER = [611, 612, 613]

/**
 * useRecalculateActivityOnDifferentDate is a custom hook designed to handle the process of recalculating activity-related
 * emissions and properties when an activity is rescheduled to a different date in a user's journey timeline. This hook focuses
 * particularly on activities where emissions or other associated data are impacted by the specific date.
 *
 * The emissions recalculated for certain activity types, such as flights, depend on the date they occur. For flights,
 * altering the flight date might also impact additional metadata like departure and arrival airports if a flight number is provided.
 *
 * The hook leverages state management, references, and several utility functions to manage the below functionality:
 * - Validate new activity dates and ensure they fall within the journey's duration in days.
 * - Recalculate emissions data by generating updated requests when a flight or activity is moved.
 * - Compare flight data returned by the emissions API for changes in departure and arrival airports.
 * - Handle successful and error states for emissions calculations, updating activities as necessary.
 * - Generate error or success toasts (if applicable) to inform the user of the recalculation status.
 *
 * This hook brings efficiency for complex date-specific recalculations by supporting asynchronous emission calculations
 * alongside error handling mechanisms for activities with mismatched or incomplete data.
 *
 * @param {UseUpdateActivityOnDifferentDateProps} options - The input properties required for the hook.
 * @param {number} options.journeyDuration - Total duration of the user's journey, usually expressed in days.
 * @param {function} options.renderToast - Callback function for rendering feedback or notifications (e.g., success or error messages).
 *
 * @returns {Object} - This hook manages and returns several properties including:
 * - `updatedActivities`: Current state of updated activities after recalculation.
 * - `recalculateActivitiesEmissionsWithUpdatedDateInActivityProps`: Function to bulk update emissions for a list of activities aligned with new dates.
 * - `recalculateActivityEmissionWitNewGivenDay`: Function to update and recalculate the emissions for a specific activity moved to a new day.
 *
 * Note: This hook is tightly coupled with the emission calculation flow and is particularly useful for applications
 * requiring real-time emission adjustments based on date changes in user itineraries.
 */
const useRecalculateActivityOnDifferentDate = ({
                                                   journeyDuration,
                                                   renderToast,
                                               }: UseUpdateActivityOnDifferentDateProps) => {
    const [updatedActivities, setUpdatedActivities] = useState<AllActivityTypesArray | null>(null)
    const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
    const [calculateEmissions, {
        isError,
        isLoading,
    }] = useCalculateEmissionsMutation()
    const ignoreRemoveFlightNumberToast = useRef(false)
    const originalActivities = useRef<AllActivityTypesArray | null>(null)
    const {t, i18n} = useTranslation()
    const {resolvedLanguage} = i18n
    const dayOfJourney = useRef<number | null>(null)

    // stands at the end of an action
    /**
     * Recalculates emissions for activities with an updated date in their properties.
     * Stands at the end of an action.
     * This function takes an array of activities, updates their dates if specified,
     * and recalculates the emissions based on the new date. The recalculation process uses
     * specific helper methods to update the activity and validate the recalculations.
     *
     * @param {AllActivityTypesArray} activities - An array of activities to be processed and recalculated.
     */
    const recalculateActivitiesEmissionsWithUpdatedDateInActivityProps = (activities: AllActivityTypesArray) => {
        originalActivities.current = activities
        for (const activity of activities) {
            const {common: {day}} = activity
            if (day !== null) {
                const updatedActivity = _updateActivityRequest(activity, day)
                if (typeof updatedActivity !== 'undefined') _checkCalculationRequestBeforeAnewCalculation(updatedActivity)
            }
        }
    }

    /**
     * Gets the new droppableZoneId (or day) of the moved activity, updates the activity request and starts the
     * recalculation process.
     *
     * @param {AllActivityTypes} activity - The activity whose emissions need to be recalculated.
     * @param {string|number} dayToWhichActivityWasMoved - The new day to which the activity has been moved. It can be a string or number.
     */
    const recalculateActivityEmissionWitNewGivenDay = (activity: AllActivityTypes, dayToWhichActivityWasMoved: string | number) => {
        originalActivities.current = cloneDeep([activity])
        if (isValidDay(dayToWhichActivityWasMoved) && journeyDurationInDays !== null) {
            let dayNumber: number
            if (typeof dayToWhichActivityWasMoved !== 'number') {
                dayNumber = parseInt(dayToWhichActivityWasMoved, 10)
            } else {
                dayNumber = dayToWhichActivityWasMoved
            }
            if (dayNumber >= 1 && dayNumber <= journeyDurationInDays) {
                dayOfJourney.current = dayNumber
                const updatedActivity = _updateActivityRequest(activity, dayNumber)
                if (typeof updatedActivity !== 'undefined') _checkCalculationRequestBeforeAnewCalculation(updatedActivity)
            }
        }
    }

    /**
     * Updates the specified activity by changing its associated date to a new day
     * and applies necessary changes based on the journey's duration.
     *
     * @param {AllActivityTypes} activity - The activity object to be updated.
     * @param {number} dayToWhichActivityWasMoved - The new day to which the activity is moved.
     * @returns {AllActivityTypes | undefined} The updated activity object or undefined if the update is not performed.
     */
    const _updateActivityRequest = (
        activity: AllActivityTypes,
        dayToWhichActivityWasMoved: number,
    ): AllActivityTypes | undefined => updateActivityRequestWithNewDate(cloneDeep(activity), journeyDuration, dayToWhichActivityWasMoved, true)

    /**
     * Asynchronously calculates the emissions for a given activity based on its type and request data.
     * Handles specific calculation logic for activity types such as flights and invokes relevant emission calculation methods.
     * If an error occurs during emissions calculation, it may trigger a recalculation process.
     *
     * @param {AllActivityTypes} activity - The activity object containing details such as activity type and request data for emissions calculation.
     * @returns {Promise<void>} A promise that resolves when the emissions calculation process and related operations are complete.
     * @throws {Error} Throws an error if emissions calculation fails or encounters unexpected conditions.
     *
     * @throws Will handle specific errors related to flight emissions recalculation through the `_recalculateActivityEmissionsOnError` function when applicable.
     */
    const _calculateActivityEmissions = async (activity: AllActivityTypes) => {
        try {
            const {common: {activityType, request}} = activity
            if (request !== null) {
                switch (activityType) {
                    case ACTIVITY_TYPE.flight:
                        if (request.every(isFlightCalculationRequestLeg)) {
                            const result = await calculateEmissions({
                                flightActivities: request,
                                uuid: activity.common.id
                            }).unwrap()
                            _handleCalculatedEmissionsResult(result, activity)
                        }
                        break
                    case ACTIVITY_TYPE.hotel:
                        break;
                    case ACTIVITY_TYPE.car_bus:
                        break;
                    default:
                        activityType satisfies never
                }
            }
        } catch (error) {
            if (isGeneralCalculatedFlightEmissionsResponse(error)) _recalculateActivityEmissionsOnError(error.data, activity)
        }
    }

    /**
     * Handles the validation and recalculation of requests based on the activity type before performing a new calculation.
     *
     * Depending on the type of activity, the function processes and validates the calculation request to ensure
     * data consistency, and performs necessary recalculations when required. It supports specific handling logic
     * for flight activity types, while hotel and car or bus activities may have their own placeholders for future
     * implementation.
     *
     * @param {AllActivityTypes} activityForCalculation - The input activity object that contains data necessary for the calculation,
     *                                                    including activity type, request details, and other related information.
     * @returns {Promise<void>} A promise that resolves once the appropriate validation and recalculation logic is executed.
     *
     * Workflow:
     * - Checks whether the `activityType` is not null.
     * - Handles different logic based on the `activityType`, such as flights, hotels, or car/bus activities.
     * - A flight requested by flight number and flight date gets its departure - and arrival airport returned by the API. However, given a different
     * flight date and flight number, the arrival - and/or departure airport could be different. Checks this
     * and removes critical props before it starts a new calculation of the emissions.
     * - Safeguards against inconsistent or invalid data by resetting specific fields in case of mismatches.
     *
     * Notes:
     * - The flight-specific handling includes verifying legs of a flight and comparing them with the original request.
     * - If validation fails for flight legs, the flight date and number are reset before recalculating emissions.
     * - Placeholder switch cases exist for hotel and car/bus activity types but are not yet implemented.
     */
    const _checkCalculationRequestBeforeAnewCalculation = async (activityForCalculation: AllActivityTypes) => {
        const {common: {activityType}} = activityForCalculation
        if (activityType !== null) {
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (activityForCalculation.common?.request?.every(isFlightCalculationRequestLeg)) {
                        const legs = activityForCalculation.common?.request
                        const hasSameLegs = await _requestFlightByFlightNumberAndDateAndCompareToOriginalRequest(legs)
                        if (hasSameLegs) {
                            _calculateActivityEmissions(activityForCalculation)
                        } else {
                            ignoreRemoveFlightNumberToast.current = true
                            const updatedActivity = cloneDeep(activityForCalculation)
                            if (updatedActivity.common?.request?.every(isFlightCalculationRequestLeg)) {
                                updatedActivity.common?.request?.forEach(leg => {
                                        leg.flightDate = null
                                        leg.flightNumber = null
                                    }
                                )
                                _calculateActivityEmissions(updatedActivity)
                            }
                        }
                    }
                    break;
                case ACTIVITY_TYPE.hotel:
                    break;
                case ACTIVITY_TYPE.car_bus:
                    break;
                default:
                    activityType satisfies never
            }
        }
    }

    /**
     * Handles the result of a calculated emissions API response and updates application state accordingly.
     *
     * @private
     * @param {CalculatedEmissionsBackendResponse} calculatedEmissions - The backend response containing calculated emissions data.
     * @param {AllActivityTypes} activityForCalculation - The activity data used during the emissions calculation.
     *
     * @description
     * This function processes the calculated emissions data based on the activity type and the status of the backend calculation result.
     * For flight activities, it handles different statuses such as success, success with errors, or failure by invoking the appropriate
     * helper functions to either recalculate emissions or update the state.
     *
     * The function expects the backend response to contain a unique identifier (`uuid`) and additional data specific to the activity type.
     * For unsupported activity types, no action is performed.
     */
    const _handleCalculatedEmissionsResult = (
        calculatedEmissions: CalculatedEmissionsBackendResponse,
        activityForCalculation: AllActivityTypes,
    ) => {
        const {uuid, status, flightActivities: calculatedFlightActivities} = calculatedEmissions
        if (uuid !== null) {
            const {common: {activityType}} = activityForCalculation
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isActivityFlightState(activityForCalculation) && calculatedFlightActivities) {
                        switch (status) {
                            case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors:
                                _recalculateActivityEmissionsOnError(calculatedEmissions, activityForCalculation)
                                break
                            case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success:
                                _updateFlightOnRecalculationSuccess(calculatedEmissions, activityForCalculation)
                                break;
                            case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed:
                                _recalculateActivityEmissionsOnError(calculatedEmissions, activityForCalculation)
                                break;
                            default:
                                status satisfies never;
                        }
                    }
                    break;
                case ACTIVITY_TYPE.hotel:
                    break;
                case ACTIVITY_TYPE.car_bus:
                    break;
                default:
                    activityType satisfies never;
            }
        }
    }

    /**
     * Asynchronously validates flight search results by comparing flights derived
     * from their flight number and date with the originally requested flight legs.
     * If the search returns different results, appropriate warnings or error
     * notifications are triggered.
     *
     * This function iterates through each leg in the original request,
     * constructs a query to fetch flights by flight number and date, performs the
     * fetch, and validates the results. It ensures flights with identical departure
     * and arrival airports match the original request. Warnings are generated for
     * cases such as:
     * - Code-sharing flights.
     * - Flights with differences in route information.
     * - Removed flight numbers or dates during recalculation.
     *
     * Error notifications are triggered for network-related issues or unexpected
     * server responses.
     *
     * @param {FlightCalculationRequestLeg[]} originalRequestLegs - Array of flight
     *        request legs representing the original flight search data.
     * @returns {Promise<boolean>} Boolean value indicating whether the newly
     *          fetched flight results are identical to the original request.
     */
    const _requestFlightByFlightNumberAndDateAndCompareToOriginalRequest = async (originalRequestLegs: FlightCalculationRequestLeg[]) => {
        let sameResultsAsOriginalSearch = false
        const originalArrivalAndDepartureIata = originalRequestLegs.flatMap(leg => [leg.departure, leg.arrival])
        for (const leg of originalRequestLegs) {
            const {flightNumber, flightDate} = leg
            if (typeof flightNumber !== 'undefined' && typeof flightDate !== 'undefined') {
                const convertedFlightDate = convertBackendStringDateToDateTime(flightDate)
                if (flightDate !== null && convertedFlightDate !== null) {
                    const query = constructGetFlightByFlightNumberQuery(flightNumber, flightDate)
                    let headers = new Headers();
                    const token = Cookies.get(COOKIE_NAME)
                    headers.set('Authorization', `Bearer ${token}`);
                    try {
                        const response = await fetch(`${GET_FLIGHTS_BY_DATE_AND_AIRLINE}?${query}`,
                            {
                                method: "GET",
                                headers
                            });
                        if (response.ok) {
                            const newFlightResults: FlightsResponse[] = await response.json();
                            const isCodeSharingFlight = newFlightResults.some(flight => flight.isNonOperationalCodeSharingFlight)
                            if (isCodeSharingFlight) {
                                renderToast(t("TOASTS.WARNING.DIFFERENT_FLIGHT_WITH_FLIGHT_NUMBER",
                                    {
                                        interpolation: {escapeValue: false},
                                        flightNumber,
                                        flightDate: formatDateString(convertedFlightDate, resolvedLanguage, false),
                                    }), "warning")
                                return false
                            } else {
                                const newArrivalAndDepartureIata = newFlightResults.flatMap(newFlightResult => [newFlightResult.departureAirport.iata, newFlightResult.arrivalAirport.iata])
                                if (newFlightResults.length >= 1) {
                                    if (isEqual(originalArrivalAndDepartureIata, newArrivalAndDepartureIata)) return true
                                    renderToast(t("TOASTS.WARNING.DIFFERENT_FLIGHT_WITH_FLIGHT_NUMBER", {
                                        interpolation: {escapeValue: false},
                                        flightNumber,
                                        flightDate: formatDateString(convertedFlightDate, resolvedLanguage, false),
                                        legs: generateConnectionStringFromCalculationResponseString(newFlightResults),
                                    }), "warning")
                                    return false
                                }
                                // handling of no result with emission calculation (i.e. showing toast that flight number
                                // and flight date have been removed) is done in here _updateFlightOnRecalculationSuccess()
                                // The update of the request is done here _handleCalculatedEmissionsResult()
                                // because of that no need to return false here
                                renderToast(t("TOASTS.WARNING.REMOVED_FLIGHT_NUMBER_ON_RECALCULATION", {
                                    flightNumber,
                                    date: formatDateString(convertedFlightDate, resolvedLanguage, false),
                                }), "warning")
                                return false
                            }
                        } else {
                            renderToast(t("TOASTS.ERROR.NO_RESULT_FLIGHT_NUMBER_AND_DATE",
                                {
                                    flightNumber,
                                    flightDate: formatDateString(convertedFlightDate, resolvedLanguage, false)
                                }), "error")
                            console.error(`Response not in range of 200 - 299. Response is ${response.status}`)
                            return true
                        }
                    } catch (error) {
                        renderToast(t("TOASTS.ERROR.NO_RESULT_FLIGHT_NUMBER_AND_DATE",
                            {
                                flightNumber,
                                flightDate: formatDateString(convertedFlightDate, resolvedLanguage, false)
                            }), "error")
                        console.error(`Something went wrong getting flights via flight number and flight date: ${error}`)
                        return true
                    }
                }
            }
        }
        return sameResultsAsOriginalSearch
    }


    /**
     * Recalculates flight emissions for a given activity if an error occurs during the emission calculation process.
     *
     * This function attempts to address cases where the calculated emissions response indicates errors
     * for one or more flight activities. It modifies the flight calculation request by removing the flight
     * numbers and associated dates from the problematic entries and then re-submits the modified request
     * for recalculation.
     *
     * @param {CalculatedEmissionsBackendResponse} calculatedEmissions - The response object containing
     * the calculated emissions data and flight activities information.
     * @param {ActivityFlightState} activityForCalculation - The state object representing the flight
     * activity to be recalculated.
     */
    const _recalculateFlightEmissionsOnError = (
        calculatedEmissions: CalculatedEmissionsBackendResponse,
        activityForCalculation: ActivityFlightState,
    ) => {
        const {
            uuid: activityId,
            status,
            flightActivities: calculatedFlightActivities
        } = calculatedEmissions
        if (
            status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors ||
            status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed
        ) {
            const updatedActivityForCalculation = cloneDeep(activityForCalculation)
            const {common: {originalRequest}} = updatedActivityForCalculation
            if (originalRequest !== null) {
                const updatedRequestForRecalculation = cloneDeep(originalRequest)
                if (updatedRequestForRecalculation.every(isFlightCalculationRequestLeg)) {
                    calculatedFlightActivities!!.forEach(calculatedFlightActivity => {
                        const {index: legIndex, errorCodes: legErrorCodes} = calculatedFlightActivity
                        if (legErrorCodes) {
                            legErrorCodes.forEach(legError => {
                                const {code: legCode} = legError
                                if (ERROR_CODES_TO_REMOVE_FLIGHT_NUMBER.includes(legCode)) {
                                    const {index: calculatedFlightActivityErrorIndex} = calculatedFlightActivity
                                    const indexToRemoveFlightNumberAndDate = updatedRequestForRecalculation
                                        .map(activity => activity.index)
                                        .indexOf(calculatedFlightActivityErrorIndex)
                                    if (indexToRemoveFlightNumberAndDate > -1) {
                                        updatedRequestForRecalculation.forEach(flightRequest => {
                                            const {index: flightRequestIndex} = flightRequest
                                            if (flightRequestIndex === indexToRemoveFlightNumberAndDate && isFlightCalculationRequestLeg(flightRequest)) {
                                                flightRequest.flightDate = null
                                                flightRequest.flightNumber = null
                                            }
                                        })
                                    }
                                    const {legs} = updatedActivityForCalculation
                                    legs[legIndex].date = null
                                    legs[legIndex].flightNumber = null
                                    const nextLegIndex = legIndex + 1
                                    if (typeof legs[nextLegIndex] !== 'undefined') {
                                        legs[nextLegIndex].flightNumber = null
                                        legs[nextLegIndex].date = null
                                    }
                                }
                            })
                        }
                    })
                    updatedActivityForCalculation.common.request = updatedRequestForRecalculation
                    _calculatedEmissionsFallback({
                            flightActivities: updatedRequestForRecalculation,
                            uuid: activityId
                        },
                        updatedActivityForCalculation,
                    )
                }
            }
        }
    }

    /**
     * Fallback function for handling emission calculations in cases of errors or exceptions during the calculation process.
     * This function attempts to handle errors specific to different activity types and updates the relevant emissions data
     * or user interface accordingly while displaying appropriate error messages.
     *
     * @param {CalculateEmissionsRequest} requestUsedForCalculation - The request object used during the emissions calculation.
     * @param {AllActivityTypes} activityForCalculation - The activity object associated with the calculation, containing type and request details.
     *
     * @throws Will show a toast notification for any calculation errors encountered, specifying the activity type.
     */
    const _calculatedEmissionsFallback = async (
        requestUsedForCalculation: CalculateEmissionsRequest,
        activityForCalculation: AllActivityTypes,
    ) => {
        try {
            const result = await calculateEmissions(requestUsedForCalculation).unwrap()
            _handleCalculatedEmissionsResult(result, activityForCalculation)
        } catch (error) {
            const {common: {activityType, request}} = activityForCalculation
            const updatedDndActivities = cloneDeep(originalActivities.current) // was dndActivitiesRef before
            const {uuid} = requestUsedForCalculation
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isGeneralCalculatedFlightEmissionsResponse(error)) {
                        const {data: {flightActivities}} = error
                        const indexOfActivityToUpdate = updatedDndActivities?.findIndex(dndActivity => dndActivity.common.id === uuid)
                        if (typeof indexOfActivityToUpdate !== 'undefined' && indexOfActivityToUpdate > -1 && updatedDndActivities) {
                            updatedDndActivities[indexOfActivityToUpdate].common.totalEmissions = flightActivities ?? null
                            updatedDndActivities[indexOfActivityToUpdate].common.request = request
                        }
                    }
                    break
                case ACTIVITY_TYPE.hotel:
                    break;
                case ACTIVITY_TYPE.car_bus:
                    break;
                    break;
                default:
                    activityType satisfies never;
            }
            renderToast(t("TOASTS.ERROR.EMISSIONS_NOT_CALCULATED_JOURNEY", {
                activityType: generateTranslatedActivityTypeString(activityType, i18n)
            }), 'error')
            setUpdatedActivities(updatedDndActivities)
        }
    }

    /**
     * Handles the recalculation of activity emissions when an error occurs.
     *
     * This function processes the calculated emissions and the activity provided for calculation.
     * It verifies the activity type and, if necessary, recalculates flight activity emissions
     * when discrepancies are identified (e.g., computation on a different date or error states
     * in the data).
     *
     * @param {CalculatedEmissionsBackendResponse | undefined} calculatedEmissions - The response containing calculated emissions data to process.
     * @param {AllActivityTypes} activityForCalculation - The activity data used for emissions calculation.
     */
    const _recalculateActivityEmissionsOnError = (
        calculatedEmissions: CalculatedEmissionsBackendResponse | undefined,
        activityForCalculation: AllActivityTypes,
    ) => {
        if (calculatedEmissions) {
            const {uuid: activityUidId, flightActivities: calculatedFlightActivities} = calculatedEmissions
            if (activityUidId) {
                const {common: {activityType}} = activityForCalculation
                if (recalculateActivityOnDifferentDate(activityType)) {
                    if (isActivityFlightState(activityForCalculation) && calculatedFlightActivities) _recalculateFlightEmissionsOnError(calculatedEmissions, activityForCalculation)
                }
            }
        }
    }

    /**
     * Updates the flight activity data after a successful recalculation of flight emissions.
     *
     * This function takes the calculated emissions response and the current flight activity state
     * to process updates, including validating and modifying emissions data, setting updated activities,
     * and displaying relevant warnings as toasts.
     *
     * The function ensures that emissions data are properly synchronized with the computed results
     * from the recalculation process. If flight details, such as flight number or date, were
     * removed or changed during recalculation, the user is warned via a toast message. Additionally,
     * errors from the recalculation process are also handled and displayed as toast notifications.
     *
     * Parameters:
     * @param {CalculatedEmissionsBackendResponse} resultCalculateEmissions - The result of the emissions calculation from the backend.
     * @param {ActivityFlightState} flightActivityForCalculation - Represents the current state of the flight activity being recalculated.
     */
    const _updateFlightOnRecalculationSuccess = (
        resultCalculateEmissions: CalculatedEmissionsBackendResponse,
        flightActivityForCalculation: ActivityFlightState,
    ) => {
        const updatedFlightActivityForCalculation = cloneDeep(flightActivityForCalculation)
        const toastHintStrings: string[] = []
        const {common: {totalEmissions}} = updatedFlightActivityForCalculation
        if (totalEmissions !== null) {
            if (isActivityFlightState(updatedFlightActivityForCalculation)) {
                const {common: {request, originalRequest}} = updatedFlightActivityForCalculation
                let flightNumberAndDateStringAdded = false
                if (originalRequest?.every(isFlightCalculationRequestLeg) && request?.every(isFlightCalculationRequestLeg)) {
                    originalRequest.forEach((originalLeg, legIndex) => {
                        const {
                            flightNumber: originalFlightNumber,
                            flightDate: originalFlightDate
                        } = originalLeg
                        if (request[legIndex]) {
                            const {
                                flightNumber: requestedFlightNumber,
                                flightDate: requestFlightDate
                            } = request[legIndex]
                            if (
                                !flightNumberAndDateStringAdded &&
                                typeof originalFlightNumber !== "undefined" &&
                                originalFlightNumber !== requestedFlightNumber &&
                                typeof originalFlightDate !== "undefined" &&
                                originalFlightDate !== requestFlightDate
                            ) {
                                if (journeyDuration.dates && dayOfJourney.current !== null && !ignoreRemoveFlightNumberToast.current) {
                                    const {dates: {startDate}} = journeyDuration
                                    toastHintStrings.push(t('TOASTS.WARNING.REMOVED_FLIGHT_NUMBER_ON_RECALCULATION', {
                                        flightNumber: originalFlightNumber,
                                        date: formatDateString(startDate.plus({days: dayOfJourney.current - 1}), resolvedLanguage, false)
                                    }))
                                }
                                flightNumberAndDateStringAdded = true
                            }
                        }
                    })
                }
            }

            const updatedOriginalActivities = cloneDeep(originalActivities.current)
            const indexOfActivityToUpdate = updatedOriginalActivities?.findIndex(updatedOriginalActivity => updatedOriginalActivity.common.id === resultCalculateEmissions.uuid)
            if (typeof indexOfActivityToUpdate !== 'undefined' && indexOfActivityToUpdate > -1 && updatedOriginalActivities) {
                // test if new emissions are correctly assigned to calculated activity
                // if (updatedFlightActivityForCalculation.common.totalEmissions && updatedFlightActivityForCalculation.common.totalEmissions[0]) updatedFlightActivityForCalculation.common.totalEmissions[0].emissionKgCO2e = 9999999
                updatedOriginalActivities[indexOfActivityToUpdate] = updatedFlightActivityForCalculation
                setUpdatedActivities(updatedOriginalActivities)
                dayOfJourney.current = null
            } else {
                renderToast(t("TOASTS.ERROR.EMISSIONS_NOT_CALCULATED_JOURNEY", {
                    activityType: generateTranslatedActivityTypeString(ACTIVITY_TYPE.flight, i18n)
                }), "error")
            }
        }
        toastHintStrings.length > 0 && renderToast(toastHintStrings.join("\n"), 'warning')
        ignoreRemoveFlightNumberToast.current = false
    }

    return {
        isLoading,
        isError,
        ignoreRemoveFlightNumberToast: ignoreRemoveFlightNumberToast.current,
        updatedActivities,
        recalculateActivitiesEmissionsWithUpdatedDateInActivityProps,
        recalculateActivityEmissionWitNewGivenDay
    }
}

export default useRecalculateActivityOnDifferentDate